// components/ActivitySelector.tsx
import React from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ResearchActivity, ResearchActivityType, UserRole } from '@avanzu/types';
import { useAuth } from '../../../contexts/AuthContext';

interface ActivitySelectorProps {
  activities: ResearchActivity[];
  selectedActivity: ResearchActivity | null;
  onActivitySelect: (activity: ResearchActivity) => void;
  onCreateActivity: () => void;
  onReorderActivities: (activities: ResearchActivity[]) => void;
}

const ActivitySelector: React.FC<ActivitySelectorProps> = ({
  activities,
  selectedActivity,
  onActivitySelect,
  onCreateActivity,
  onReorderActivities,
}) => {
  const { currentOrganisation } = useAuth();
  const isViewer = currentOrganisation?.role === UserRole.VIEWER;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedActivities = activities.sort((a, b) => a.sortOrder - b.sortOrder);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortedActivities.findIndex((item) => item.id === active.id);
      const newIndex = sortedActivities.findIndex((item) => item.id === over?.id);

      const reorderedActivities = arrayMove(sortedActivities, oldIndex, newIndex);

      // Update sortOrder for all activities
      const updatedActivities = reorderedActivities.map((activity, index) => ({
        ...activity,
        sortOrder: index,
        updatedAt: new Date(),
      }));

      onReorderActivities(updatedActivities);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Create Button */}
      {!isViewer && (
        <Box sx={{ mb: 2, pl: 1.5, pr: 1.5 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateActivity} fullWidth>
            Create New Activity
          </Button>
        </Box>
      )}

      {/* Activities List with Drag and Drop */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <DndContext
          sensors={isViewer ? [] : sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortedActivities} strategy={verticalListSortingStrategy}>
            <List disablePadding>
              {sortedActivities.map((activity) => (
                <SortableActivityItem
                  key={activity.id}
                  activity={activity}
                  isSelected={selectedActivity?.id === activity.id}
                  onSelect={onActivitySelect}
                  isViewer={isViewer}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </Box>

      {/* Info Note */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'info.lighter',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'info.light',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="info.dark">
          Note: Activities with allocated transactions cannot be deleted.
        </Typography>
      </Box>
    </Box>
  );
};

interface SortableActivityItemProps {
  activity: ResearchActivity;
  isSelected: boolean;
  onSelect: (activity: ResearchActivity) => void;
  isViewer: boolean;
}

const SortableActivityItem: React.FC<SortableActivityItemProps> = ({
  activity,
  isSelected,
  onSelect,
  isViewer,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: activity.id,
    disabled: isViewer,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <ListItem ref={setNodeRef} style={style} disablePadding sx={{ mb: 1 }}>
      <ListItemButton
        selected={isSelected}
        onClick={() => !isDragging && onSelect(activity)}
        sx={{
          borderRadius: 1,
          border: activity.type === ResearchActivityType.CORE ? 2 : 1,
          borderColor: activity.type === ResearchActivityType.CORE ? 'primary.main' : 'divider',
          backgroundColor: '#ffffff',
          '&.Mui-selected': {
            backgroundColor: 'action.selected',
            borderColor: 'primary.main',
          },
        }}
      >
        {!isViewer && (
          <IconButton
            size="small"
            sx={{
              mr: 1,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              '&:hover': { backgroundColor: 'transparent' },
            }}
            {...attributes}
            {...listeners}
          >
            <DragIcon fontSize="small" />
          </IconButton>
        )}

        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2" noWrap>
                {activity.name}
              </Typography>
              <Chip
                label={activity.type.toUpperCase()}
                size="small"
                color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              Budget: {formatCurrency(activity.budgetAmount)} | Actual:{' '}
              {formatCurrency(activity.actualAmount)}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};

export default ActivitySelector;
